import GraphRect from "./graph_rect";
import GraphContainer, { getOrSet } from "./graph_container";
import GraphGroup from "./graph_group";
import createTextFilter from "./create-text-filter";

class GraphAxis extends GraphRect {
    _showAxisX;
    _showAxisY;
    _colorText;
    _colorTextShadow;
    _xLabel;
    _yLabel;
    _showAxisTitles;

    constructor(parent) {
        super();

        this._parent = parent;

        const { feFlood, filter } = createTextFilter(this._uuid);

        this._feFlood = feFlood;
        this._filter = filter;

        this._defs.append(this._filter);

        this._lines = new GraphGroup()
            .setAttribute("name", "grid-lines");
        //.setAttribute("clip-path", `url(#clip_${this._uuid})`);

        this.append(this._lines);

        function createAxis() {
            return GraphContainer
                .create("text")
                .setAttribute("font-size", "2rem")
                .setAttribute("dominant-baseline", "middle")
                .setAttribute("text-anchor", "middle");
        }

        this._editField = GraphContainer
            .create(document.createElement("input"))
            .setAttribute("placeholder", "Axis label")
            .setAttribute("pointer-events", "all")
            .setAttribute("width", 121)
            .setAttribute("height", 21)
            .addListener("change", this._onLabelChange.bind(this))
            .addListener("keyup", this._onLabelChange.bind(this));

        this._foreignObject = GraphContainer
            .create("foreignObject")
            .setAttribute("width", 121)
            .setAttribute("height", 21)
            .setAttribute("display", "none")
            .setAttribute("pointer-events", "all")
            .append(this._editField);

        this._xAxis = createAxis();
        this._yAxis = createAxis();
        this._activeAxis = "x";

        this._lines.append(this._xAxis, this._yAxis, this._foreignObject);

        [this._xAxis, this._yAxis]
            .forEach(ax => ax.addListener("click", this._onClickLabel.bind(this, ax)));
    }

    _onLabelChange(event) {
        switch (this._activeAxis) {
            case "x":
                this._parent.parent.xLabel = event.target.value;
                this._parent.xLabel(this._parent.parent.xLabel);
                break;
            case "y":
                this._parent.parent.yLabel = event.target.value;
                this._parent.yLabel(this._parent.parent.yLabel);
                break;
            default: throw new Error("Unknown axis");
        }

        if (event.keyCode === 13) this._parent.stopEditing();
    }

    /**
     * 
     * @param {GraphContainer} label 
     * @param {MouseEvent} event 
     */
    _onClickLabel(label, event) {
        event.preventDefault();

        this._parent.stopEditing();
        this._parent.editState.editingLabel = true;

        const object = this._editField.getObject(0);

        if (label === this._xAxis) {
            const label = this._parent.parent.xLabel, len = label.length;

            this._xAxis.setAttribute("display", "none");
            this._foreignObject
                .removeAttribute("display")
                .setAttribute("x", this._origin.x + (this._size.width) * 0.5 + 8 - 60.5)
                .setAttribute("y", this._origin.y + this._size.y - 12 - 10.5)

            object.value = label;
            object.setSelectionRange(len, len);
            this._activeAxis = "x";
        } else if (label === this._yAxis) {
            const label = this._parent.parent.yLabel, len = label.length;
            const x = this._origin.x - 121 + 21;
            const y = this._origin.y + this._size.y * 0.5 - 12 - 10.5;

            this._yAxis.setAttribute("display", "none");
            this._foreignObject
                .removeAttribute("display")
                .setAttribute("x", Math.max(0, x))
                .setAttribute("y", y);

            object.value = label;
            object.setSelectionRange(len, len);
            this._activeAxis = "y";
        } else throw new Error("Unknown axis");

        object.focus();

    }

    /**
     * @returns {this}
     */
    xLabel() { return getOrSet.call(this, this, "_xLabel", ...arguments); }
    /**
     * @returns {this}
     */
    yLabel() { return getOrSet.call(this, this, "_yLabel", ...arguments); }

    /**
     * @returns {this}
     */
    showAxisX() { return getOrSet.call(this, this, "_showAxisX", ...arguments); }

    /**
     * @returns {this}
     */
    showAxisY() { return getOrSet.call(this, this, "_showAxisY", ...arguments); }

    /**
     * @returns {this}
     */
    colorText() { return getOrSet.call(this, this, "_colorText", ...arguments); }

    /**
     * @returns {this}
     */
    colorTextShadow() { return getOrSet.call(this, this, "_colorTextShadow", ...arguments); }

    /**
     * @returns {this}
     */
    showAxisTitles() { return getOrSet.call(this, this, "_showAxisTitles", ...arguments); }

    redraw() {
        super.redraw();

        this._parent.editState.editingLabel = false;

        this._feFlood.setAttribute("flood-color", this._colorTextShadow);
        this._foreignObject.setAttribute("display", "none");

        if (this._showAxisX && this._showAxisTitles && (Q3.editMode || this._xLabel.length > 0)) {
            this._xAxis
                .removeAttribute("display")
                .setAttribute("fill", this._xLabel.length > 0 ? this._colorText : "rgba(128, 128, 128, 0.3)")
                .setAttribute("pointer-events", "all")
                .setAttribute("filter", `url(#fill_text_${this._uuid})`)
                .setAttribute("x", this._origin.x + (this._size.width) * 0.5 + 8)
                .setAttribute("y", this._origin.y + this._size.y - 12)
                .innerText(this._xLabel.length > 0 ? this._xLabel : "X Label");
        } else this._xAxis.setAttribute("display", "none");

        if (this._showAxisY && this._showAxisTitles && (Q3.editMode || this._yLabel.length > 0)) {
            this._yAxis
                .removeAttribute("display")
                .setAttribute("fill", this._yLabel.length > 0 ? this._colorText : "rgba(128, 128, 128, 0.3)")
                .setAttribute("pointer-events", "all")
                .setAttribute("filter", `url(#fill_text_${this._uuid})`)
                .setAttribute("style", `transform: rotate(-90deg) translate(${this._size.height * -0.5 + 24 - this._origin.y - 8}px, ${this._origin.x + 12}px)`)
                .innerText(this._yLabel.length > 0 ? this._yLabel : "Y Label");
        } else this._yAxis.setAttribute("display", "none");

        return this;
    }
}

export default GraphAxis;
export { GraphAxis };